import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';
import {
  IDenoisedFileMetricsResponse,
  IFileInfo,
  IFileListResponse,
  IFileMetricsResponse,
  IFilemeasurement,
  ISampleMetricsResponse,
} from '../../interfaces';
import { environment as env } from 'src/environment/environment';
import { shakeParams } from '../../classes';
import { IListResponse } from 'src/app/core';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private readonly _http: HttpClient = inject(HttpClient);

  private idSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  /**
   * Retrieves a list of files from the server.
   * @param params - The parameters to include in the request.
   * @returns An Observable that emits an IFileListResponse object.
   */
  getAll(params: any): Observable<IFileListResponse> {
    params = shakeParams(params);

    return this._http.get<IFileListResponse>(`${env.apiGateway}/file`, {
      params,
    });
  }

  getAllDenoised(params: any): Observable<IDenoisedFileMetricsResponse> {
    params = shakeParams(params);

    return this._http
      .get<IDenoisedFileMetricsResponse>(`${env.apiGateway}/file/denoised`, {
        params,
      })
      .pipe(
        catchError(error => {
          console.error(error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Sets the currently selected file ID.
   * @param id - The ID of the file to set as the current selection.
   */
  setId(id: number) {
    this.idSubject.next(id);
  }

  /**
   * Gets an observable for the currently selected file ID.
   * @returns An Observable that emits the selected file ID.
   */
  getId(): Observable<number> {
    return this.idSubject.asObservable();
  }

  /**
   * Retrieves file metrics for a specific file and time range.
   * @param id - The ID of the patient's file.
   * @param startDate - The start date for the metrics.
   * @param endDate - The end date for the metrics.
   * @returns An Observable that emits an IFileMetricsResponse object.
   */
  getMetrics(
    id: number,
    startDate: string,
    endDate: string
  ): Observable<IFileMetricsResponse> {
    const params = new HttpParams().appendAll({
      patientId: id,
      startDate: startDate,
      endDate: endDate,
    });

    return this._http.get<IFileMetricsResponse>(
      `${env.apiGateway}/file/metrics`,
      {
        params,
      }
    );
  }

  /**
   * Retrieves detailed information about a specific file.
   * @param id - The ID of the patient's file.
   * @returns An Observable that emits an IFileInfo object.
   */
  getFileInfo(id: number): Observable<IFileInfo> {
    const params = new HttpParams().appendAll({
      patientFileId: id,
    });

    return this._http.get<IFileInfo>(`${env.apiGateway}/file/info`, { params });
  }

  getMeasurements(params: any): Observable<IListResponse<IFilemeasurement>> {
    params = shakeParams(params);

    return this._http.get<IListResponse<IFilemeasurement>>(
      `${env.apiGateway}/file/measurements`,
      { params }
    );
  }

  getSamplesMetrics(id: number): Observable<ISampleMetricsResponse> {
    return this._http.get<ISampleMetricsResponse>(
      `${env.apiGateway}/file/${id}`
    );
  }

  /**
   * Downloads the PCG audio recording for a patient file (m4a / AAC).
   * @param id - The patient file ID.
   */
  getAudio(id: number): Observable<Blob> {
    return this._http.get(`${env.apiGateway}/file/audio/${id}`, {
      responseType: 'blob',
    });
  }

  deleteFile(id: number): Observable<void> {
    return this._http.delete<void>(`${env.apiGateway}/file/${id}`);
  }
}
